import { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

import type { TypeBadge } from "../../core/types/control";
import { TypeBadge as BadgeType } from "../../core/types/control";

const BADGE_COLOR_BY_TYPE: Record<TypeBadge, string> = {
	[BadgeType.BRONZE]: "var(--badge-bronze-stroke, #BF8970)",
	[BadgeType.SILVER]: "var(--badge-silver-stroke, #C0C0C0)",
	[BadgeType.GOLD]: "var(--badge-gold-stroke, #D4AF37)",
};

const normalizeBadgeType = (type?: string | null): TypeBadge => {
	const normalized = type?.trim().toLowerCase();

	if (normalized === BadgeType.GOLD.toLowerCase()) return BadgeType.GOLD;
	if (normalized === BadgeType.SILVER.toLowerCase()) return BadgeType.SILVER;
	return BadgeType.BRONZE;
};

const isSvgUrl = (url: string) => {
	if (!url) return false;
	if (url.startsWith("data:image/svg+xml")) return true;
	const clean = url.split("?")[0].split("#")[0].toLowerCase();
	return clean.endsWith(".svg");
};

const isTintablePaintValue = (value?: string | null) => {
	const normalized = value?.trim().toLowerCase();
	if (!normalized) return false;
	if (normalized === "none" || normalized === "transparent") return false;
	if (normalized.startsWith("url(")) return false;
	return true;
};

const applyPaint = (element: SVGElement, paintType: "fill" | "stroke", color: string) => {
	element.setAttribute(paintType, color);
	if (paintType === "fill") {
		element.style.fill = color;
		return;
	}
	element.style.stroke = color;
};

type BadgeIconProps = {
	src?: string | null;
	type?: string | null;
	alt: string;
	className?: string;
	placeholderClassName?: string;
	isSvgSource?: boolean;
};

export function BadgeIcon({
	src,
	type,
	alt,
	className = "w-full h-full object-contain",
	placeholderClassName = "w-full h-full rounded-md bg-slate-100",
	isSvgSource = false,
}: BadgeIconProps) {
	const [svgFailed, setSvgFailed] = useState(false);

	useEffect(() => {
		setSvgFailed(false);
	}, [src]);

	if (!src) {
		return <div className={placeholderClassName} />;
	}

	const badgeType = normalizeBadgeType(type);
	const tintColor = BADGE_COLOR_BY_TYPE[badgeType];
	const shouldUseSvg = (isSvgSource || isSvgUrl(src)) && !svgFailed;
	const iconScaleClass = "scale-[1.12] origin-center";

	const handleBeforeInjection = (svg: SVGSVGElement) => {
		svg.setAttribute("width", "100%");
		svg.setAttribute("height", "100%");
		svg.setAttribute("color", tintColor);
		svg.setAttribute("stroke", tintColor);
		svg.style.color = tintColor;
		svg.style.stroke = tintColor;

		svg.querySelectorAll<SVGElement>("[stroke]").forEach((element) => {
			const stroke = element.getAttribute("stroke")?.trim().toLowerCase();
			if (!isTintablePaintValue(stroke)) {
				return;
			}
			applyPaint(element, "stroke", tintColor);
		});

		svg.querySelectorAll<SVGElement>("[fill]").forEach((element) => {
			const fill = element.getAttribute("fill")?.trim().toLowerCase();
			if (!isTintablePaintValue(fill)) {
				return;
			}
			applyPaint(element, "fill", tintColor);
		});
	};

	const renderLoading = () => <div className={placeholderClassName} />;

	const renderFallback = () => (
		<img src={src || ""} alt={alt} className={`${className} ${iconScaleClass}`} />
	);

	const handleError = () => {
		setSvgFailed(true);
	};

	if (shouldUseSvg) {
		return (
			<ReactSVG
				src={src}
				beforeInjection={handleBeforeInjection}
				loading={renderLoading}
				fallback={renderFallback}
				onError={handleError}
				wrapper='div'
				className='w-full h-full [&>div]:w-full [&>div]:h-full [&_svg]:w-full [&_svg]:h-full [&_svg]:scale-[2] [&_svg]:origin-center'
			/>
		);
	}

	return <img src={src} alt={alt} className={`${className} ${iconScaleClass}`} />;
}
